import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_POLL_INTERVAL_MS = 30_000;
const MIN_POLL_INTERVAL_MS = 1_000;

export interface UsePollingOptions {
  intervalMs?: number;
  fallbackErrorMessage?: string;
}

export interface UsePollingReturn<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePolling<T>(
  fetcher: () => Promise<T>,
  options?: UsePollingOptions
): UsePollingReturn<T> {
  const intervalMs = Math.max(
    options?.intervalMs ?? DEFAULT_POLL_INTERVAL_MS,
    MIN_POLL_INTERVAL_MS
  );
  const fallbackError = options?.fallbackErrorMessage ?? "Failed to load data";

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);

  const load = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const result = await fetcher();
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : fallbackError);
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [fetcher, fallbackError]);

  const startPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      void load();
    }, intervalMs);
  }, [load, intervalMs]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    setIsLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    void load();
    startPolling();

    return () => {
      stopPolling();
    };
  }, [load, startPolling, stopPolling]);

  useEffect(() => {
    function handleVisibilityChange(): void {
      if (document.hidden) {
        stopPolling();
      } else {
        void load();
        startPolling();
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [load, startPolling, stopPolling]);

  return { data, isLoading, error, refresh };
}
